import { ShoppingCartOutlined } from '@ant-design/icons';
import { usePageTitle } from '@hooks';
import { L } from '@lib';
import { GetAllPurchaseOrdersInput, PurchaseOrder } from '@services';
import { useStore } from '@stores';
import { Button, Modal } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PurchaseOrdersTablePresentation } from './PurchaseOrdersTable.presentation';
import type { PurchaseOrderEditDrawerHandle } from '../PurchaseOrderEditDrawer';
import { PurchaseOrderEditDrawer } from '../PurchaseOrderEditDrawer';
import type { ReceivePOModalHandle } from '../ReceivePOModal';
import { ReceivePOModal } from '../ReceivePOModal';
import type { POInvoiceReceiveDrawerHandle } from '../POInvoiceReceiveDrawer';
import { POInvoiceReceiveDrawer } from '../POInvoiceReceiveDrawer';

interface IProps {
  /** When true, renders without the page-level header actions (for embedding) */
  embedded?: boolean;
  /** Optional header slot for parent to inject "Receive from invoice" button */
  extraHeaderActions?: React.ReactNode;
}

export const PurchaseOrdersTable: React.FC<IProps> = observer(({ embedded, extraHeaderActions }) => {
  const { purchaseOrderStore } = useStore();
  const { setHeaderActions } = usePageTitle();

  const editDrawerRef = useRef<PurchaseOrderEditDrawerHandle>(null);
  const receiveModalRef = useRef<ReceivePOModalHandle>(null);
  const invoiceDrawerRef = useRef<POInvoiceReceiveDrawerHandle>(null);

  const [filters, setFilters] = useState<GetAllPurchaseOrdersInput>({
    skipCount: 0,
    maxResultCount: 15,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(
    (f: GetAllPurchaseOrdersInput) => purchaseOrderStore.getAll(f),
    [purchaseOrderStore],
  );

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  // CRITICAL: memoize so SharedTable's useEffect dep doesn't loop.
  const headerActions = useMemo(
    () => (
      <div style={{ display: 'flex', gap: 8 }}>
        {extraHeaderActions}
        <Button icon={<ShoppingCartOutlined />} onClick={() => invoiceDrawerRef.current?.open()}>
          {L('Receive from invoice')}
        </Button>
        <Button icon={<ShoppingCartOutlined />} type="primary" onClick={() => editDrawerRef.current?.openNew()}>
          {L('New PO')}
        </Button>
      </div>
    ),
    [extraHeaderActions],
  );

  useEffect(() => {
    if (embedded) return;
    setHeaderActions(headerActions);
    return () => setHeaderActions(null);
  }, [embedded, setHeaderActions, headerActions]);

  const handlePageChange = useCallback(
    (pagination: { current: number }) => {
      const skipCount = (pagination.current - 1) * filters.maxResultCount;
      setCurrentPage(pagination.current);
      setFilters((prev) => ({ ...prev, skipCount }));
    },
    [filters.maxResultCount],
  );

  const handleSearch = useCallback((value: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, search: value || undefined, skipCount: 0 }));
  }, []);

  const handleStatusFilter = useCallback((value: string | undefined) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, status: value, skipCount: 0 }));
  }, []);

  const handleSupplierFilter = useCallback((value: string | undefined) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, supplierId: value, skipCount: 0 }));
  }, []);

  const handleEdit = useCallback((item: PurchaseOrder) => {
    editDrawerRef.current?.open(item);
  }, []);

  const handleMarkOrdered = useCallback(
    (item: PurchaseOrder) => {
      Modal.confirm({
        title: L('Mark as ordered?'),
        content: `"${item.partName}" will be placed with ${item.supplierName ?? 'the supplier'}.`,
        okText: L('Mark as ordered'),
        cancelText: L('Go back'),
        onOk: () => purchaseOrderStore.markOrdered(item.id),
      });
    },
    [purchaseOrderStore],
  );

  // Ordered items open in the drawer which now hosts the receive form inline.
  const handleReceive = useCallback((item: PurchaseOrder) => {
    editDrawerRef.current?.open(item);
  }, []);

  const handleCancel = useCallback(
    (item: PurchaseOrder) => {
      Modal.confirm({
        title: L('Cancel purchase order?'),
        content: `"${item.partName}" will be canceled.`,
        okText: L('Cancel PO'),
        okType: 'danger',
        cancelText: L('Go back'),
        onOk: () => purchaseOrderStore.cancel(item.id),
      });
    },
    [purchaseOrderStore],
  );

  const handleSaved = useCallback(() => load(filters), [load, filters]);

  return (
    <>
      <PurchaseOrdersTablePresentation
        items={purchaseOrderStore.items}
        total={purchaseOrderStore.total}
        loading={purchaseOrderStore.loading}
        currentPage={currentPage}
        maxResultCount={filters.maxResultCount}
        headerActions={embedded ? headerActions : undefined}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onStatusFilter={handleStatusFilter}
        onSupplierFilter={handleSupplierFilter}
        onEdit={handleEdit}
        onMarkOrdered={handleMarkOrdered}
        onReceive={handleReceive}
        onCancel={handleCancel}
      />
      <PurchaseOrderEditDrawer ref={editDrawerRef} onSaved={handleSaved} />
      <ReceivePOModal ref={receiveModalRef} onSaved={handleSaved} />
      <POInvoiceReceiveDrawer ref={invoiceDrawerRef} onSaved={handleSaved} />
    </>
  );
});
